import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Mail, X, Clock, CheckCircle } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface EmailVerificationBannerProps {
  onDismiss?: () => void;
  className?: string;
}

export const EmailVerificationBanner = ({ onDismiss, className }: EmailVerificationBannerProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isResending, setIsResending] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Don't show if user is verified or banner is dismissed
  if (!user || user.email_confirmed_at || dismissed) {
    return null;
  }

  const handleResendEmail = async () => {
    setIsResending(true);
    
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: user.email!,
        options: {
          emailRedirectTo: `${window.location.origin}/email-confirmed`
        }
      });

      if (error) {
        toast({
          title: "Fehler beim Senden",
          description: "Die E-Mail konnte nicht erneut gesendet werden. Versuchen Sie es später erneut.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "E-Mail gesendet",
          description: "Bestätigungs-E-Mail wurde erneut gesendet. Bitte bestätige dein Konto innerhalb von 72 Stunden.",
        });
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive",
      });
    }
    
    setIsResending(false);
  };

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  return (
    <Alert className={`bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800 ${className}`}>
      <Mail className="h-4 w-4 text-blue-600 dark:text-blue-400" />
      <AlertDescription className="flex items-center justify-between w-full">
        <div className="flex items-center gap-3">
          <div>
            <div className="flex items-center gap-2 font-medium text-blue-900 dark:text-blue-100">
              <Clock className="h-4 w-4" />
              E-Mail-Bestätigung ausstehend
            </div>
            <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
              Bitte bestätigen Sie Ihre E-Mail-Adresse für vollen Zugriff auf alle Funktionen.
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleResendEmail}
            disabled={isResending}
            className="text-blue-700 border-blue-300 hover:bg-blue-100 dark:text-blue-300 dark:border-blue-600 dark:hover:bg-blue-900"
          >
            {isResending ? "Senden..." : "E-Mail erneut senden"}
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="text-blue-600 hover:bg-blue-100 dark:text-blue-400 dark:hover:bg-blue-900"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
};

// Hook für E-Mail Bestätigungsstatus
export const useEmailVerification = () => {
  const { user } = useAuth();
  
  return {
    isEmailVerified: user?.email_confirmed_at != null,
    isLoggedIn: !!user,
    email: user?.email,
    needsVerification: !!user && !user.email_confirmed_at,
  };
};